/**
 * Helpers for the WhatsApp canonical message types.
 *
 * Canonical values follow the Cloud API contract (text, image, audio, video,
 * document, sticker, location, contacts, template, interactive, button,
 * reaction, order, system, unknown).
 *
 * Chat rows keep the legacy `mediatype` column (raw mimetype) for
 * compatibility: whenever a canonical `msgtype` is present it wins, otherwise
 * the type is derived from the `mediatype` substring, exactly like the old
 * chat items used to do.
 */

export const MESSAGE_TYPES = [
  "text",
  "image",
  "audio",
  "video",
  "document",
  "sticker",
  "location",
  "contacts",
  "template",
  "interactive",
  "button",
  "reaction",
  "order",
  "system",
  "unknown"
];

const MEDIA_MESSAGE_TYPES = ["image", "audio", "video", "document", "sticker"];

export function isMediaMessageType(msgtype) {
  return MEDIA_MESSAGE_TYPES.indexOf(msgtype) >= 0;
}

export function normalizeMsgType(value) {
  if (typeof value !== "string") {
    return null;
  }

  const type = value.trim().toLowerCase();

  if (!type) {
    return null;
  }

  if (MESSAGE_TYPES.indexOf(type) >= 0) {
    return type;
  }

  // tolerate a couple of legacy/alternate spellings
  switch (type) {
    case "contact":
    case "vcard":
      return "contacts";
    case "buttons":
      return "button";
    case "sticker_message":
    case "animated_sticker":
      return "sticker";
    default:
      return null;
  }
}

export function msgTypeFromMimetype(mimetype) {
  const mime = typeof mimetype === "string" ? mimetype.toLowerCase() : "";

  if (mime.indexOf("image") >= 0) {
    return "image";
  }

  if (mime.indexOf("audio") >= 0) {
    return "audio";
  }

  if (mime.indexOf("video") >= 0) {
    return "video";
  }

  return "document";
}

export function msgTypeFromFile(file) {
  if (file && typeof file.type === "string" && file.type) {
    return msgTypeFromMimetype(file.type);
  }

  return "document";
}

export function parseMessagePayload(payload) {
  if (!payload) {
    return null;
  }

  if (typeof payload === "object") {
    return payload;
  }

  if (typeof payload !== "string") {
    return null;
  }

  try {
    const parsed = JSON.parse(payload);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function getMessageLocation(item) {
  const payload = parseMessagePayload(item?.payload);
  const location = payload?.location || payload || {};

  const latitude = toNumber(item?.latitude ?? location.latitude);
  const longitude = toNumber(item?.longitude ?? location.longitude);

  if (latitude === null || longitude === null) {
    return null;
  }

  return {
    latitude,
    longitude,
    name: location.name || item?.name || "",
    address: location.address || item?.address || ""
  };
}

export function getMessageLocationLink(location) {
  if (!location) {
    return "";
  }

  return "https://www.google.com/maps/search/?api=1&query=" + location.latitude + "," + location.longitude;
}

function vcardField(vcard, field) {
  if (typeof vcard !== "string") {
    return "";
  }

  const line = vcard
    .split(/\r?\n/)
    .find((row) => row.toUpperCase().indexOf(field.toUpperCase() + ":") === 0);

  if (!line) {
    return "";
  }

  return line.substring(line.indexOf(":") + 1).trim();
}

/**
 * Accepts both shapes the API can return:
 * - outbound: payload.contacts = [vCard string, ...]
 * - inbound (Cloud API): contacts = [{ name: { formatted_name }, phones: [{ phone }] }]
 */
export function getMessageContacts(item) {
  const payload = parseMessagePayload(item?.payload);
  const raw = payload?.contacts;

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((contact) => {
      if (typeof contact === "string") {
        return {
          name: vcardField(contact, "FN"),
          phone: vcardField(contact, "TEL"),
          vcard: contact
        };
      }

      if (!contact || typeof contact !== "object") {
        return null;
      }

      const phone =
        (Array.isArray(contact.phones) && contact.phones[0]?.phone) ||
        contact.phone ||
        contact.wa_id ||
        "";

      return {
        name: contact.name?.formatted_name || contact.name || contact.fname || "",
        phone,
        vcard: contact.vcard || ""
      };
    })
    .filter((contact) => contact && (contact.name || contact.phone || contact.vcard));
}

export function getMessageCaption(item) {
  const payload = parseMessagePayload(item?.payload);
  return item?.caption || payload?.caption || "";
}

export function getMessageFilename(item) {
  const payload = parseMessagePayload(item?.payload);
  return item?.filename || payload?.filename || "";
}

export function getMessageSize(item) {
  const payload = parseMessagePayload(item?.payload);
  const size = item?.size ?? payload?.size;
  const number = toNumber(size);
  return number === null ? null : number;
}

export function formatFileSize(bytes) {
  const number = toNumber(bytes);

  if (number === null || number < 0) {
    return "";
  }

  if (number < 1024) {
    return number + " B";
  }

  const units = ["KB", "MB", "GB", "TB"];
  let value = number / 1024;
  let unit = 0;

  while (value >= 1024 && unit < units.length - 1) {
    value = value / 1024;
    unit++;
  }

  return (value >= 10 ? Math.round(value) : Math.round(value * 10) / 10) + " " + units[unit];
}

export function getMessageReaction(item) {
  const payload = parseMessagePayload(item?.payload);

  if (payload?.emoji) {
    return payload.emoji;
  }

  if (payload?.reaction?.emoji) {
    return payload.reaction.emoji;
  }

  return typeof item?.message === "string" ? item.message : "";
}

export function getMessageTemplate(item) {
  const payload = parseMessagePayload(item?.payload);
  const template = payload?.template || payload || {};

  return {
    name: template.name || item?.template_name || "",
    language: template.language?.code || template.language || "",
    body: template.body || payload?.text || "",
    components: template.components || []
  };
}

export function getMessageButtons(item) {
  const payload = parseMessagePayload(item?.payload);
  const buttons = payload?.interactive?.action?.buttons || payload?.buttons;

  if (!Array.isArray(buttons)) {
    return [];
  }

  return buttons
    .map((button) => (typeof button === "string" ? button : button?.reply?.title || button?.title || button?.text || ""))
    .filter((label) => !!label);
}

/**
 * Canonical msgtype of a chat row, with a legacy fallback based on the
 * current `mediatype` substring behaviour.
 */
export function resolveMessageType(item) {
  const explicit = normalizeMsgType(item?.msgtype);

  if (explicit) {
    return explicit;
  }

  if (item?.media) {
    const mediatype = typeof item?.mediatype === "string" ? item.mediatype.toLowerCase() : "";

    if (mediatype.indexOf("image") >= 0) {
      return "image";
    }

    if (mediatype.indexOf("audio") >= 0) {
      return "audio";
    }

    if (mediatype.indexOf("video") >= 0) {
      return "video";
    }

    return "document";
  }

  if (getMessageLocation(item)) {
    return "location";
  }

  if (getMessageContacts(item).length > 0) {
    return "contacts";
  }

  const payload = parseMessagePayload(item?.payload);

  if (payload?.emoji || payload?.reaction) {
    return "reaction";
  }

  if (payload?.template) {
    return "template";
  }

  if (payload?.interactive || payload?.buttons) {
    return "interactive";
  }

  if (typeof item?.message === "string" && item.message.trim()) {
    return "text";
  }

  return "unknown";
}

const WA_STATUS_MAP = {
  sent: "sent",
  accepted: "sent",
  delivered: "delivered",
  read: "read",
  played: "read",
  failed: "failed",
  failure: "failed",
  error: "failed",
  rejected: "failed"
};

export function normalizeWastatus(value) {
  if (typeof value !== "string") {
    return null;
  }

  const status = value.trim().toLowerCase();
  return WA_STATUS_MAP[status] || null;
}

/**
 * Delivery status used for the ticks. Only `wastatus` (load or MQTT
 * `/wastatus`) produces a tick: when it is absent nothing is rendered, so the
 * legacy readstatus 0/1 UI keeps working as before.
 */
export function getDeliveryStatus(item) {
  const status = normalizeWastatus(item?.wastatus);

  if (status) {
    return status;
  }

  const payload = parseMessagePayload(item?.payload);
  return normalizeWastatus(payload?.status);
}

export const WA_STATUS_LABEL = {
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Failed"
};

const ATTACHMENT_FIELDS = [
  "msgtype",
  "caption",
  "filename",
  "size",
  "latitude",
  "longitude",
  "replyto_waid",
  "media_id",
  "payload"
];

/**
 * Adds the canonical attachment fields to the multipart body of
 * `POST chat/send`, without touching the legacy fields
 * (lineid, contactid, message, media, isgroup).
 */
export function appendAttachmentFields(formData, attachment, options = {}) {
  const data = attachment || {};

  const values = {
    msgtype: data.msgtype || (data.file ? msgTypeFromFile(data.file) : "text"),
    filename: data.filename,
    size: data.size,
    latitude: data.latitude,
    longitude: data.longitude,
    replyto_waid: data.replyto_waid,
    media_id: data.media_id,
    payload: data.payload
  };

  ATTACHMENT_FIELDS.forEach((field) => {
    const value = values[field];

    if (value === undefined || value === null || value === "") {
      return;
    }

    formData.append(field, typeof value === "object" ? JSON.stringify(value) : value);
  });

  return formData;
}
